import React from "react";
import "./PlayWalletSelector.css";
import { Icon } from "@/components/Icon";
import { cn } from "@/lib/utils";
import { fmtAmtNum } from "@/lib/money";

function WalletSegment({ cur, label, icon, selClass, iconClass, amount, value, showAmounts, testidPrefix, onChange, segBase }) {
  const on = value === cur;
  const showAmt = showAmounts && amount != null;
  return (
    <button
      type="button"
      data-testid={`${testidPrefix}-${cur}`}
      aria-pressed={on}
      data-wallet-currency={cur}
      onClick={() => onChange && onChange(cur)}
      className={cn(
        segBase,
        // With a balance shown, left-align, tighten the padding/gap so the number
        // gets maximum room (fits 6-figure balances even on a 390px phone chip),
        // and clip at the pill edge so a huge balance can never spill outside.
        showAmt ? "justify-start gap-1 px-2.5 overflow-hidden" : "justify-center",
        on ? selClass : "border-surface-border bg-surface-4 text-meta hover:text-white"
      )}
    >
      {icon && <Icon name={icon} size={15} className={cn("shrink-0", on ? iconClass : "")} />}
      {showAmt ? (
        // Stack the amount over the label so the number gets the FULL chip width.
        // The old inline layout gave the fixed-width label + icon priority and
        // truncated a 5-figure PCoins balance down to an ellipsis ("100…").
        <span className="flex min-w-0 flex-col items-start leading-[1.1]">
          <span className="tabular-nums whitespace-nowrap">{amount}</span>
          <span className="text-[9px] font-black uppercase tracking-[0.06em] opacity-80">{label}</span>
        </span>
      ) : (
        <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.06em]">{label}</span>
      )}
    </button>
  );
}

/* PlayWalletSelector — the play-wallet "selecting" control (PREMIUM_UI_DIRECTION §4.3).

   This is the deliberate counterpart to the header balance pill (which is a calm, neutral
   *viewing* read-out). This control is where the player SELECTS the wallet they are about to
   play with, so it carries the emphatic selected-green states:

   - PCoins selected  → soft green    (bg-pnr-green/10, green text, inset green hairline)
   - PCash  selected  → SOLID green   (the single surface where green fills solid)
   - Bonus  selected  → subtle white  (only rendered when bonus > 0)
   - Unselected       → neutral surface-4

   It drives the SAME currency state the Casino already uses ("coins" | "cash" | "bonus") so
   it is a drop-in replacement for the ad-hoc toggles and can be reused in the game view,
   bet slip, and anywhere a play-currency choice is made.

   Props:
   - value: "coins" | "cash" | "bonus"
   - onChange(next): called with the new currency key
   - coins / cash / bonus: numeric balances (for the inline amount + to decide if Bonus shows)
   - showBonus (default: derived from bonus > 0)
   - showAmounts (default true): show the tabular-nums balance inside each segment
   - mask: optional streamer-mode mask fn (e.g. (s) => streamerMode ? "•••" : s)
   - caption (default true): render the "Betting with X. Tap to switch." helper line
   - size: "md" (default) | "sm"
   - testidPrefix (default "pw"): data-testid prefix for each segment
   - className */
export default function PlayWalletSelector({
  value = "coins",
  onChange,
  coins = 0,
  cash = 0,
  bonus = 0,
  showBonus,
  showAmounts = true,
  mask = (s) => s,
  caption = true,
  size = "md",
  testidPrefix = "pw",
  className,
}) {
  const bonusVisible = showBonus != null ? showBonus : Number(bonus || 0) > 0;
  const h = size === "sm" ? "h-9" : "h-10";
  const txt = size === "sm" ? "text-[12px]" : "text-[13px]";

  const segBase = cn(
    "play-wallet-segment flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border px-3 font-bold transition-all duration-[var(--dur-base)] focus-ring",
    h,
    txt
  );

  // fmtAmtNum groups thousands ("10,046.26") to match the header balance pill — the
  // old toFixed(2) rendered "10046.26" ungrouped.
  const fmtCoins = showAmounts ? mask(fmtAmtNum(coins, "coins")) : null;
  const fmtCash = showAmounts ? mask(fmtAmtNum(cash, "cash")) : null;
  const fmtBonus = showAmounts ? mask(fmtAmtNum(bonus, "bonus")) : null;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="play-wallet-selector flex items-center gap-1.5 rounded-full border border-surface-border bg-surface-2 p-1"
        data-size={size}
        role="group"
        aria-label="Play wallet"
        data-testid={`${testidPrefix}-selector`}
      >
        <WalletSegment value={value} showAmounts={showAmounts} testidPrefix={testidPrefix} onChange={onChange} segBase={segBase}
          cur="coins"
          label="PCoins"
          icon="pnrcoin"
          amount={fmtCoins}
          // Soft green — the free / provably-fair currency.
          selClass="border-pnr-green bg-pnr-green/10 text-pnr-green shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]"
          iconClass="text-pnr-green"
        />
        <WalletSegment value={value} showAmounts={showAmounts} testidPrefix={testidPrefix} onChange={onChange} segBase={segBase}
          cur="cash"
          label="PCash"
          icon="pnrcash"
          amount={fmtCash}
          // Solid green — the one surface the design lets green fill solid.
          selClass="border-pnr-green bg-pnr-green text-surface-1"
          iconClass="text-surface-1"
        />
        {bonusVisible && (
          <WalletSegment value={value} showAmounts={showAmounts} testidPrefix={testidPrefix} onChange={onChange} segBase={segBase}
            cur="bonus"
            label="Bonus"
            amount={fmtBonus}
            selClass="border-surface-border bg-white/5 text-pnr-green"
          />
        )}
      </div>
      {caption && (
        <p className="flex items-center gap-1.5 px-1 text-[12px] text-meta">
          Betting with
          {value !== "bonus" && (
            <Icon
              name={value === "cash" ? "pnrcash" : "pnrcoin"}
              size={14}
              className={value === "cash" ? "text-white" : "text-pnr-green"}
            />
          )}
          <b className={value === "cash" ? "text-white" : "text-pnr-green"}>
            {value === "coins" ? "PCoins" : value === "bonus" ? "Bonus Funds (non-withdrawable)" : "PCash"}
          </b>
          . Tap to switch.
        </p>
      )}
    </div>
  );
}
